<script lang="js" setup>
import { computed, ref, watch } from 'vue'
import { get, set, useDevicePixelRatio, useElementBounding, useEventListener, useMouseInElement, watchDebounced } from '@vueuse/core'
import { useSpotStore } from '~/store/spot'
import { depthChartData } from '~/apis/spotSocket'
import { add, div, fix, mul, sub, toHuman } from '@/utils/math'

const spot = useSpotStore()

const el = ref()
const tip = ref()
const canvas = ref()
const canvas1 = ref()
const detailData = ref()
const { locale } = useI18n()
const { width, height } = useElementBounding(el)
const { pixelRatio } = useDevicePixelRatio()
const { elementX, elementY, isOutside } = useMouseInElement(canvas)
const { width: tipW, height: tipH } = useElementBounding(tip)

const chartData = computed(() => {
  let { asks, bids, id } = get(depthChartData)
  if (id === spot.id) {
    const firstA = asks.at(0)
    const firstB = bids.at(0)
    let mid
    if (firstA && firstB) {
      mid = add(firstA[0], firstB[0]).div(2)
    }
    else if (firstA) {
      mid = firstA[0]
    }
    else if (firstB) {
      mid = firstB[0]
    }
    asks = parseData(asks, mid)
    bids = parseData(bids, mid)
    const lastA = asks.at(-1)
    const lastB = bids.at(-1)
    let diff = 0
    let maxP
    let minP
    if (mid) {
      if (lastA) {
        diff = sub(lastA[0], mid).abs()
        maxP = lastA[0]
      }
      if (lastB) {
        diff = Math.max(diff, sub(lastB[0], mid).abs())
        minP = lastB[0]
      }
      if (!minP) {
        minP = diff ? sub(mid, diff) : 0
      }
      if (!maxP) {
        maxP = diff ? add(mid, diff) : mul(mid, 2)
      }
    }
    const maxA = Math.max(asks.reduce((sofar, cur) => add(cur[1], sofar), 0), bids.reduce((sofar, cur) => add(cur[1], sofar), 0))
    const maxAText = toHuman(maxA, spot.as)
    const minPText = fix(minP, spot.ps)
    const maxPText = fix(maxP, spot.ps)
    return {
      mid: +mid,
      diff,
      maxA,
      asks,
      bids,
      minPText,
      maxAText,
      maxPText,
    }
  }
  return null
})
const options = computed(() => {
  const colors = getColors()
  const cvs = get(canvas)
  /** @type CanvasRenderingContext2D */
  const ctx = cvs?.getContext('2d')
  /** @type CanvasRenderingContext2D */
  const ctx1 = get(canvas1)?.getContext('2d')
  const px = get(pixelRatio)
  return {
    ctx,
    ctx1,
    px,
    width: get(width) * px,
    height: get(height) * px,
    ruleHeight: 28,
    // theme: rootStore.theme,
    ...colors,
  }
})
const tipPosition = computed(() => {
  const data = get(detailData)
  const opt = get(options)
  const maxW = get(width)
  const maxH = get(height)
  const w = get(tipW)
  const h = get(tipH)

  if (data) {
    const { x1, y1 } = data
    let x = x1 / opt.px
    let y = y1 / opt.px
    if (x + w > maxW) {
      x = x - w
    }
    if (y + h > maxH) {
      y = y - h - 20
    }
    x = Math.max(0, x)
    y = Math.max(0, y + 10)

    return {
      x,
      y,
    }
  }
  return {}
})

const ashes = {}
function getColors() {
  const css = getComputedStyle(document.documentElement)
  return {
    bg: css.getPropertyValue('--bg').trim(),
    rise: css.getPropertyValue('--buy').trim(),
    fall: css.getPropertyValue('--sell').trim(),
    border: css.getPropertyValue('--lineBorder').trim(),
    t1: css.getPropertyValue('--t1').trim(),
    t2: css.getPropertyValue('--t2').trim(),
    bgRise: css.getPropertyValue('--functionalGreen1').trim(),
    bgFall: css.getPropertyValue('--functionalRed1').trim(),
  }
}
const p = n => Math.floor(n) + 0.5
const sortAxis = () => ashes.axis.sort((a, b) => a.x1 - b.x1)
function clear(ctx) {
  const opt = get(options)
  ctx.clearRect(0, 0, opt.width, opt.height)
}

function drawRules() {
  const opt = get(options)
  const data = get(chartData)
  if (!data) {
    return
  }
  // const pt = opt.ruleHeight * 2 * opt.px;
  const pr = 4 * opt.px
  const pl = 8 * opt.px
  const minWidth = 34 * opt.px

  opt.ctx.fillStyle = opt.t2
  opt.ctx.font = `${12 * opt.px}px Roboto`
  opt.ctx.textAlign = 'left'
  opt.ctx.textBaseline = 'middle'

  ashes.axis = []
  ashes.xLabelWidth = Math.max(opt.ctx.measureText(data.maxPText).width, minWidth * 2)
  ashes.yLabelWidth = data.maxA === 0 ? minWidth : Math.max(opt.ctx.measureText(toHuman(data.maxAText, spot.as)).width, minWidth)
  ashes.chartW = p(opt.width - ashes.yLabelWidth - pl - pr)
  ashes.chartH = p(opt.height - opt.ruleHeight * opt.px)
  /**
   * 0/9: 边缘座标
   * 1/8: 画图区域座标
   * 5: 中心点座标
   * A: 有效区域宽高
   * F: 整个区域宽高
   * A2: 有效区域宽高的一半
   */
  ashes.x0 = 0
  ashes.x1 = ashes.xLabelWidth + pr
  ashes.x9 = ashes.chartW - opt.px
  ashes.x8 = ashes.x9 - ashes.x1
  ashes.xA = ashes.x8 - ashes.x1
  ashes.xF = ashes.x9 - ashes.x0
  ashes.xA2 = ashes.xA / 2
  ashes.x5 = ashes.xA2 + ashes.x1
  ashes.y0 = 0
  ashes.y1 = ashes.x1
  ashes.y9 = ashes.chartH - opt.px
  ashes.yA = ashes.y9 - ashes.y1
  ashes.yF = ashes.y9 - ashes.y0

  ashes.xLabelY = ashes.chartW + pl // Y轴文字的x
  ashes.yLabelX = ashes.chartH + pl // X轴文字的y

  opt.ctx.fillStyle = opt.t2
  opt.ctx.fillText(0, ashes.xLabelY, ashes.y9)
  if (data.maxA !== 0) {
    let n = [5, 4, 3].reduce((sofar, cur) => {
      if (data.maxA % cur === 0) {
        sofar = cur
      }
      return sofar
    }, 5)
    const tick = div(data.maxA, n)
    if (tick < Number(`1e-${spot.as}`)) {
      let changed = false
      for (let i = n; i > 1; i--) {
        if (data.maxA % i === 0) {
          n = i
          changed = true
          break
        }
      }
      if (!changed) {
        n = 1
      }
    }
    const tickN = div(data.maxA, n)
    const tickH = div(ashes.yA, n)
    while (n--) {
      const t = n + 1
      const y = p(ashes.y9 - tickH * t)
      opt.ctx.fillText(t === n ? data.maxAText : toHuman(mul(tickN, t), spot.as), ashes.xLabelY, y)
      // opt.ctx.strokeStyle = opt.border;
      // opt.ctx.rect(ashes.chartW, y, 4 * opt.px, 0);
      // opt.ctx.stroke();
    }
    opt.ctx.textAlign = 'center'
    opt.ctx.textBaseline = 'top'
    opt.ctx.fillText(fix(data.mid, spot.ps), ashes.x5, ashes.yLabelX)
    if (data.diff > data.mid) {
      const x = ashes.x1 + ashes.xA2 * (data.diff - data.mid) / data.diff
      opt.ctx.fillText(0, x, ashes.yLabelX)
      // opt.ctx.fillText('x', ashes.xStart, ashes.xTick);
    }
    else if (data.diff === 0) {
      opt.ctx.fillText(0, ashes.x1, ashes.yLabelX)
    }
    else {
      opt.ctx.fillText(data.minPText, ashes.x1, ashes.yLabelX)
    }
    opt.ctx.fillText(data.maxPText, ashes.x8, ashes.yLabelX)
  }

  opt.ctx.strokeStyle = opt.border
  opt.ctx.beginPath()
  opt.ctx.moveTo(ashes.chartW, 0)
  opt.ctx.lineTo(ashes.chartW, ashes.chartH)
  opt.ctx.lineTo(0, ashes.chartH)
  opt.ctx.stroke()
  opt.ctx.closePath()
}
function drawMountain(key, strokeStyle, fillStyle) {
  const opt = get(options)
  const data = get(chartData)
  const { ctx } = opt
  if (!data || !data.mid) {
    return
  }
  const { mid, diff, [key]: arr } = data
  const isAsks = key === 'asks'
  const x9 = isAsks ? ashes.x9 : ashes.x0
  ctx.strokeStyle = strokeStyle
  ctx.lineWidth = mul(1, opt.px).toFixed(0)
  ctx.beginPath()
  let xLast
  let yLast = ashes.y9
  if (arr.length) {
    let total = 0
    for (const o of arr) {
      const [price, amount] = o
      let xDiff = price - mid
      if (xDiff !== 0) {
        xDiff = xDiff / diff * ashes.xA2
      }
      xLast = ashes.x5 + xDiff
      total = add(total, amount)
      const x = p(xLast)
      const y = p(yLast)
      ctx.lineTo(x, y)
      ctx.lineTo(x, p(yLast = yLast - ashes.yA * amount / data.maxA))

      ashes.axis.push({
        x1: x,
        y1: p(yLast),
        strokeStyle,
        price,
        total,
        isAsks,
      })
      // ctx.fillText(price, xLast, yLast - 50);
    }
    ctx.lineTo(x9, yLast)
    ctx.stroke()
    ctx.lineTo(x9, p(ashes.y9))
    ctx.closePath()
    ctx.fillStyle = fillStyle
    ctx.fill()
  }
}
function drawCross() {
  const opt = get(options)
  const { ctx, ctx1 } = opt
  const xPointer = get(elementX) * opt.px
  const yPointer = get(elementY) * opt.px
  if (get(isOutside) || !ctx || !ctx1 || xPointer > ashes.x9 || yPointer > ashes.y9) {
    return
  }
  clear(ctx1)
  const gap = 5 * opt.px
  const r = 10 * opt.px

  let temp
  if (ashes.axis) {
    for (const o of ashes.axis) {
      if (o.isAsks) {
        if (xPointer >= o.x1) {
          temp = o
        }
      }
      else {
        if (xPointer < o.x1 && !temp) {
          temp = o
        }
      }
    }
  }

  set(detailData, temp)
  if (!temp) {
    return
  }

  const x = p(temp.x1)
  const y = p(temp.y1)

  ctx1.strokeStyle = temp.strokeStyle
  ctx1.fillStyle = temp.strokeStyle
  ctx1.lineWidth = ctx.lineWidth

  ctx1.setLineDash([gap, gap])

  ctx1.beginPath()
  ctx1.arc(x, y, gap, 0, 2 * Math.PI)
  ctx1.closePath()
  ctx1.fill()
  ctx1.beginPath()
  ctx1.moveTo(x, y)
  ctx1.lineTo(x, p(ashes.y9))
  ctx1.stroke()
  ctx1.closePath()
}
function drawAll() {
  const opt = get(options)
  const data = get(chartData)
  if (!opt.ctx || !data) {
    return
  }
  opt.ctx.clearRect(0, 0, opt.width, opt.height)
  opt.ctx.lineWidth = opt.px
  drawRules()
  drawMountain('asks', opt.fall, opt.bgFall)
  drawMountain('bids', opt.rise, opt.bgRise)
  sortAxis()
  drawCross()
}

useEventListener(canvas1, 'mousemove', drawCross)
watch(isOutside, () => {
  clear(get(options).ctx1)
  set(detailData, null)
})

watch([pixelRatio, locale], drawAll)
watchDebounced([width, height, depthChartData, options], drawAll, {
  debounce: 50,
})

function parseData(data = [], mid) {
  const obj = data.reduce((sofar, next) => {
    sofar.total = add(next[1], sofar.total)
    const price = next[0]
    const item = [price, sofar.total]

    // 取[-5%, 5%]深度图数据
    if (Math.abs(+price - mid) / mid <= 0.05) {
      sofar.list.push(item)
    }
    return sofar
  }, { total: 0, list: [] })

  return obj.list
}
</script>

<template>
  <div ref="el" class="relative flex-1 w-full h-full">
    <canvas
      ref="canvas"
      class="w-full h-full"
      :width="width * pixelRatio"
      :height="height * pixelRatio"
    />
    <canvas
      ref="canvas1"
      class="absolute inset-0 w-full h-full"
      :width="width * pixelRatio"
      :height="height * pixelRatio"
    />
    <div
      v-if="detailData"
      ref="tip"
      class="absolute top-0 px-4 py-3 space-y-2 text-xs border-l-2 shadow border-border bg-fillPagePrimary"
      :class="{ 'border-fall': detailData.isAsks }"
      :style="{
        left: `${tipPosition.x}px`,
        top: `${tipPosition.y}px`,
      }"
    >
      <div>{{ $t('价格') }}: {{ fix(detailData.price, spot.ps) }} {{ spot.quote }}</div>
      <div>{{ $t('累计量') }}: {{ fix(detailData.total, spot.as) }} {{ spot.base }}</div>
    </div>
  </div>
</template>
